<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { formatPrice } from '@/utils/formatPrice';
import { arrow_right, delete_product } from '@/assets/svg/common.svg';

const { t } = useI18n({ useScope: 'global' });

const props = defineProps<{
  col: { id: string | null; name: string; products: any[] };
  colIndex: number;
  isExpanded: boolean;
  isEditing: boolean;
  dragOverKey: string | null;
  isDraggingProduct: boolean;
  editCatLang: 'fr' | 'nl' | 'en';
  editCatDesigFr: string;
  editCatDesigNl: string;
  editCatDesigEn: string;
  editCatDescFr: string;
  editCatDescNl: string;
  editCatDescEn: string;
  editCatModules: string[];
  isSavingCatEdit: boolean;
  isAutoTranslatingCatDesig: boolean;
  isAutoTranslatingCatDesc: boolean;
  totalCount: number;
  movingCategoryId: string | null;
  deletingProductId: string | null;
}>();

const emit = defineEmits<{
  (e: 'toggle'): void;
  (e: 'start-edit'): void;
  (e: 'cancel-edit'): void;
  (e: 'save-edit'): void;
  (e: 'delete'): void;
  (e: 'move-up'): void;
  (e: 'move-down'): void;
  (e: 'remove-product', productId: string): void;
  (e: 'dragover'): void;
  (e: 'dragleave'): void;
  (e: 'drop'): void;
  (e: 'update:editCatLang', val: 'fr' | 'nl' | 'en'): void;
  (e: 'update:editCatDesigFr', val: string): void;
  (e: 'update:editCatDesigNl', val: string): void;
  (e: 'update:editCatDesigEn', val: string): void;
  (e: 'update:editCatDescFr', val: string): void;
  (e: 'update:editCatDescNl', val: string): void;
  (e: 'update:editCatDescEn', val: string): void;
  (e: 'update:editCatModules', val: string[]): void;
  (e: 'cat-desig-input', lang: 'fr' | 'nl' | 'en', val: string): void;
  (e: 'cat-desc-input', lang: 'fr' | 'nl' | 'en', val: string): void;
}>();

const colKey = () => props.col.id ?? props.col.name;

const MODULE_OPTIONS = [
  {
    id: 'click_and_collect',
    icon: 'pi-shopping-cart',
    labelKey: 'commerceDetail.modulesCnc',
  },
  {
    id: 'pos_register',
    icon: 'pi-calculator',
    labelKey: 'commerceDetail.modulesCaisse',
  },
  {
    id: 'pds_display',
    icon: 'pi-desktop',
    labelKey: 'commerceDetail.modulesPds',
  },
  { id: 'kiosk', icon: 'pi-tablet', labelKey: 'commerceDetail.modulesKiosk' },
] as const;
function toggleModule(list: string[], id: string) {
  const idx = list.indexOf(id);
  const next = [...list];
  if (idx >= 0) next.splice(idx, 1);
  else next.push(id);
  return next;
}
</script>

<template>
  <div
    class="border-b border-gray-100 transition-all duration-200 last:border-0"
    :class="
      dragOverKey === colKey()
        ? 'bg-primary-900/8 border-primary-900/30 scale-[1.01] shadow-sm'
        : isDraggingProduct
          ? 'hover:bg-primary-900/5'
          : ''
    "
    @dragover.prevent="emit('dragover')"
    @dragenter.prevent
    @dragleave="emit('dragleave')"
    @drop.prevent="emit('drop')"
  >
    <!-- Drop overlay indicator -->
    <div
      v-if="dragOverKey === colKey()"
      class="border-primary-900 mx-2 my-1 flex items-center justify-center gap-1.5 rounded-lg border border-dashed py-1.5"
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 14 14"
        fill="none"
        class="text-primary-900"
      >
        <path
          d="M7 1v8M4 6l3 3 3-3"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M2 11h10"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />
      </svg>
      <span class="text-primary-900 font-tt-medium-Normal text-[10px]"
        >Déposer ici</span
      >
    </div>

    <!-- Edit mode -->
    <div
      v-else-if="isEditing"
      class="flex flex-col gap-3 border-b border-gray-100 bg-gray-50 px-3 py-3"
      @click.stop
    >
      <div class="flex items-center justify-between">
        <p class="text-13px font-tt-semiBold-Normal text-grand-text">
          Modifier la catégorie
        </p>
        <select
          :value="editCatLang"
          @change="
            emit(
              'update:editCatLang',
              ($event.target as HTMLSelectElement).value as 'fr' | 'nl' | 'en',
            )
          "
          class="text-11px text-secondary-text cursor-pointer rounded-lg border border-gray-200 bg-white px-1.5 py-0.5 outline-none"
        >
          <option value="fr">FR</option>
          <option value="nl">NL</option>
          <option value="en">EN</option>
        </select>
      </div>

      <!-- Désignation -->
      <div class="flex flex-col gap-1">
        <p
          class="text-11px font-tt-medium-Normal text-secondary-text tracking-wide uppercase"
        >
          Nom
        </p>
        <div
          v-for="lang in ['fr', 'nl', 'en'] as const"
          :key="lang"
          class="flex items-center gap-1.5"
        >
          <span
            class="text-11px font-tt-medium-Normal w-5 shrink-0"
            :class="
              editCatLang === lang ? 'text-primary-900' : 'text-secondary-text'
            "
            >{{ lang.toUpperCase() }}</span
          >
          <div
            v-if="editCatLang !== lang && isAutoTranslatingCatDesig"
            class="border-t-primary-900 h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-gray-200"
          />
          <input
            :value="
              lang === 'fr'
                ? editCatDesigFr
                : lang === 'nl'
                  ? editCatDesigNl
                  : editCatDesigEn
            "
            @input="
              emit(
                'cat-desig-input',
                lang,
                ($event.target as HTMLInputElement).value,
              );
              lang === 'fr'
                ? emit(
                    'update:editCatDesigFr',
                    ($event.target as HTMLInputElement).value,
                  )
                : lang === 'nl'
                  ? emit(
                      'update:editCatDesigNl',
                      ($event.target as HTMLInputElement).value,
                    )
                  : emit(
                      'update:editCatDesigEn',
                      ($event.target as HTMLInputElement).value,
                    );
            "
            type="text"
            :placeholder="
              lang === 'fr' ? 'Nom FR' : lang === 'nl' ? 'Naam NL' : 'Name EN'
            "
            class="text-12px text-grand-text flex-1 rounded-lg border px-2 py-1.5 outline-none"
            :style="{
              borderColor: editCatLang === lang ? '#F89303' : '#e5e7eb',
            }"
          />
        </div>
      </div>

      <!-- Description -->
      <div class="flex flex-col gap-1">
        <p
          class="text-11px font-tt-medium-Normal text-secondary-text tracking-wide uppercase"
        >
          Description
        </p>
        <div
          v-for="lang in ['fr', 'nl', 'en'] as const"
          :key="lang"
          class="flex items-center gap-1.5"
        >
          <span
            class="text-11px font-tt-medium-Normal w-5 shrink-0"
            :class="
              editCatLang === lang ? 'text-primary-900' : 'text-secondary-text'
            "
            >{{ lang.toUpperCase() }}</span
          >
          <div
            v-if="editCatLang !== lang && isAutoTranslatingCatDesc"
            class="border-t-primary-900 h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-gray-200"
          />
          <input
            :value="
              lang === 'fr'
                ? editCatDescFr
                : lang === 'nl'
                  ? editCatDescNl
                  : editCatDescEn
            "
            @input="
              emit(
                'cat-desc-input',
                lang,
                ($event.target as HTMLInputElement).value,
              );
              lang === 'fr'
                ? emit(
                    'update:editCatDescFr',
                    ($event.target as HTMLInputElement).value,
                  )
                : lang === 'nl'
                  ? emit(
                      'update:editCatDescNl',
                      ($event.target as HTMLInputElement).value,
                    )
                  : emit(
                      'update:editCatDescEn',
                      ($event.target as HTMLInputElement).value,
                    );
            "
            type="text"
            :placeholder="
              lang === 'fr'
                ? 'Description FR'
                : lang === 'nl'
                  ? 'Beschrijving NL'
                  : 'Description EN'
            "
            class="text-12px text-grand-text flex-1 rounded-lg border px-2 py-1.5 outline-none"
            :style="{
              borderColor: editCatLang === lang ? '#F89303' : '#e5e7eb',
            }"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <p
          class="text-11px font-tt-medium-Normal text-secondary-text tracking-wide uppercase"
        >
          {{ t('allCollections.modulesLabel') }}
        </p>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="m in MODULE_OPTIONS"
            :key="m.id"
            type="button"
            @click.stop="
              emit(
                'update:editCatModules',
                toggleModule([...editCatModules], m.id),
              )
            "
            class="text-11px font-tt-medium-Normal flex items-center gap-1 rounded-lg border px-2 py-1"
            :class="
              editCatModules.includes(m.id)
                ? 'border-primary-900 bg-primary-900/10 text-grand-text'
                : 'text-secondary-text border-gray-200'
            "
          >
            <i :class="['pi', m.icon]" style="font-size: 0.6875rem" />
            {{ t(m.labelKey) }}
          </button>
        </div>
      </div>

      <div class="flex gap-2">
        <button
          @click.stop="emit('cancel-edit')"
          class="text-12px text-grand-text font-tt-medium-Normal h-8 flex-1 rounded-lg border border-gray-200 hover:bg-gray-100"
        >
          {{ t('catalogue.collections.cancel') }}
        </button>
        <button
          @click.stop="emit('save-edit')"
          :disabled="isSavingCatEdit"
          class="text-12px font-tt-medium-Normal h-8 flex-1 rounded-lg text-white disabled:opacity-60"
          style="background: linear-gradient(135deg, #333 0%, #111 100%)"
        >
          {{ isSavingCatEdit ? '...' : t('catalogue.collections.save') }}
        </button>
      </div>
    </div>

    <!-- Display mode -->
    <div
      v-else
      class="group flex w-full items-center justify-between px-3 py-4 hover:bg-gray-50"
    >
      <div
        @click="emit('toggle')"
        class="flex w-full flex-1 flex-row items-center justify-between"
      >
        <p class="text-14px font-tt-medium-Normal text-grand-text">
          {{ col.name }}
        </p>
        <div
          v-html="arrow_right"
          class="shrink-0 transition-transform duration-200"
          :class="isExpanded ? 'rotate-90' : ''"
        />
      </div>

      <button
        @click.stop="emit('start-edit')"
        class="text-secondary-text hover:text-primary-900 ml-2 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
      >
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <path
            d="M9 1.5L11.5 4L4.5 11H2V8.5L9 1.5Z"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <button
        @click.stop="emit('move-up')"
        :disabled="colIndex === 0 || !!movingCategoryId"
        class="text-secondary-text hover:text-primary-900 ml-1 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 disabled:cursor-default disabled:opacity-25"
      >
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <path
            d="M6.5 10V3M3.5 6L6.5 3L9.5 6"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <button
        @click.stop="emit('move-down')"
        :disabled="colIndex >= totalCount - 1 || !!movingCategoryId"
        class="text-secondary-text hover:text-primary-900 ml-1 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 disabled:cursor-default disabled:opacity-25"
      >
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <path
            d="M6.5 3V10M3.5 7L6.5 10L9.5 7"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <button
        @click.stop="emit('delete')"
        class="text-secondary-text ml-1 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 hover:text-[#E63D4B]"
      >
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <path
            d="M2 3.5h9M5 3.5V2.5h3v1M5.5 6v4M7.5 6v4M3 3.5l.5 7.5h6l.5-7.5"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <!-- Products expanded -->
    <div v-if="isExpanded" class="space-y-2 px-3 pb-2">
      <div
        v-for="product in col.products"
        :key="product.id"
        class="bg-fond-claire flex items-center gap-2 rounded-xl p-2"
      >
        <div class="flex w-full flex-1 items-center justify-between gap-2">
          <div class="flex w-full flex-1 flex-row items-center gap-3">
            <div
              class="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-gray-200"
            >
              <img
                v-if="product?.image?.info?.url"
                :src="product?.image?.info?.url"
                :alt="product?.designation"
                class="h-full w-full object-cover"
              />
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex w-full items-center justify-between gap-1">
                <p
                  class="text-grand-text text-12px font-tt-medium-Normal line-clamp-1"
                >
                  {{ product?.designation }}
                </p>
                <p
                  class="text-grand-text text-12px font-tt- font-tt-medium-Normal shrink-0"
                >
                  {{ formatPrice(Number(product?.price)) }}
                </p>
              </div>
              <p
                class="text-secondary-text line-clamp-1 text-[10px] leading-tight"
              >
                {{ product?.description }}
              </p>
            </div>
          </div>
          <button
            @click.stop="emit('remove-product', product.id)"
            :disabled="deletingProductId === product.id"
            class="text-secondary-text shrink-0 transition-colors hover:text-[#E63D4B] disabled:opacity-40"
          >
            <div v-html="delete_product" />
          </button>
        </div>
      </div>
      <p
        v-if="!col.products.length"
        class="text-secondary-text text-12px py-2 text-center"
      >
        Aucun produit
      </p>
    </div>
  </div>
</template>
